import { observable, computed, action } from 'mobx'

import BaseListStore, { IBaseListAddOptions } from 'stores/base/BaseListStore'
import ModelStore from 'stores/model/ModelStore'
import ScriptStore from 'stores/script/ScriptStore'
import SessionStore from 'stores/session/SessionStore'
import { MODEL_LIST_SESSION_ID } from 'stores/session/Session'
import ActionStore from 'stores/action/ActionStore'
import LayoutStore from 'stores/layout/LayoutStore'

import createError from 'utils/createError'

import Tab, { MODEL_LIST_TAB_ID, ITabCreatePayload } from './Tab'

export type TAB_DIRECTION = 'left' | 'right'

class TabStore extends BaseListStore<Tab, ITabCreatePayload> {
  @observable activeTabId: string

  constructor() {
    super(Tab, { idbTableName: 'tabs' })

    // Always have the `model list` tab in here, since you cannot close this
    SessionStore.add(
      {
        id: MODEL_LIST_SESSION_ID,
        type: 'model-list',
        scriptId: null,
        lastSavedHash: null,
      },
      { skipPersist: true }
    )
    super.add(
      {
        id: MODEL_LIST_TAB_ID,
        sessionId: MODEL_LIST_SESSION_ID,
        preview: false,
      },
      { skipPersist: true }
    )

    this.activeTabId = MODEL_LIST_TAB_ID
  }

  @computed
  get activeTab(): Tab | null {
    return this.get(this.activeTabId)
  }

  @computed
  get openTabs(): Tab[] {
    const tabs = Object.values(this.values)
    return tabs
      .reverse() // Tabs will come back in the order they were created, but we want to show them in the opposite order
      .filter((tab): tab is Tab => !!tab && !!tab.session)
      .sort(tab => (tab.id === MODEL_LIST_TAB_ID ? 1 : -1)) // Reorder model list tab to the end, every time
  }

  @computed
  get previewTab(): Tab | null {
    return Object.values(this.values).find(t => t.preview) || null
  }

  @action
  hydrate = ({ activeTabId }: { activeTabId: string }) => {
    // Also make sure the activeTabId still exists, before switching to it
    let activeTab = this.get(activeTabId)

    if (!activeTab || !activeTab.session) {
      // If the requested active tab does not exist, switch to the model list tab
      this.switch({ id: MODEL_LIST_TAB_ID })
      return
    }

    // It is possible that an open tab's model was deleted, so we cannot just open a tab. We must remove such tabs.
    Object.values(this.values).forEach(tab => {
      if (!tab.session) {
        this.remove(tab.id)
        return
      }

      if (tab.session.isScript) {
        const scriptModel = ModelStore.get(tab.session.script.modelId)
        if (!scriptModel) {
          this.remove(tab.id)
          return
        }
      }
    })

    // Now, let's try to activate a tab
    activeTab = this.get(activeTabId)
    if (!activeTab || !activeTab.session) {
      // Check again to make sure active tab still exists
      this.switch({ id: MODEL_LIST_TAB_ID })
      return
    }

    // If it does, switch to it
    this.switch({ id: activeTabId })
  }

  @action
  add(
    {
      modelId,
      scriptId,
      sessionId,
      ...tabProps
    }: Omit<ITabCreatePayload, 'id' | 'sessionId'> & {
      modelId?: string
      scriptId?: string
      sessionId?: string
    },
    options: IBaseListAddOptions = {}
  ): Tab {
    // To create a tab, we need a session
    // To create a session, we need a script
    // To create a script, we need a model
    // So the creation order goes: model > script > session (with the possibility of jumping in at any step)

    // So that's what this method allows you to do. Given at least one of `modelId`, `scriptId`, or `sessionId`, you can create a tab

    let tab: Tab

    if (!sessionId && !scriptId && !modelId) {
      throw createError('TabStore.add', 'Invaid params', {
        modelId,
        scriptId,
        sessionId,
      })
    }

    if (modelId) {
      const model = ModelStore.get(modelId)
      if (!model)
        throw createError('TabStore.add', 'Invalid modelId', {
          modelId,
        })

      scriptId = ScriptStore.add({
        frozen: true,
        name: null,
        modelId,
        fieldIds: model.fieldIds,
      }).id
    }

    if (scriptId) {
      sessionId = SessionStore.add({
        type: 'script',
        scriptId,
        lastSavedHash: null,
      }).id
    }

    if (sessionId) {
      tab = super.add(
        {
          ...tabProps,
          sessionId,
        },
        options
      )
    }

    return tab!
  }

  /**
   * Switches to a tab by tabId or scriptId. Creates a new tab if needed.
   */
  @action
  switch({
    id,
    index,
    direction,
  }: {
    id?: string
    index?: number
    direction?: TAB_DIRECTION
  }) {
    if (!id && index === undefined && direction === undefined) {
      throw createError('TabStore.switch', 'Invalid params', {
        id,
        index,
        direction,
      })
    }

    let nextActiveTab
    if (id) {
      // If an ID is given, it means the consumer wants me to switch to that tab exactly, no funny business
      nextActiveTab = this.get(id)
    } else if (index !== undefined) {
      // If an index is given, it means the consumer wants me to switch to that tab exactly, no funny business
      nextActiveTab = this.openTabs[index]
    }

    if (direction) {
      const activeTabIndex = this.openTabs.findIndex(
        t => t.id === this.activeTabId
      )
      if (activeTabIndex !== -1) {
        if (direction === 'right') {
          nextActiveTab = this.openTabs[activeTabIndex + 1]
          if (activeTabIndex === this.openTabs.length - 1) {
            // If the active tab is ModelListTab, go to the first tab
            nextActiveTab = this.openTabs[0]
          }
        } else if (direction === 'left') {
          // If the active tab is index 0, this will be undefined, fallback to ModelListTab
          nextActiveTab = this.openTabs[activeTabIndex - 1]
        }
      }
    }

    // If neither `id` nor `index` is provided, we'll just switch to the model list tab
    if (!nextActiveTab) {
      nextActiveTab = this.get(MODEL_LIST_TAB_ID)!
    }

    // At this point, nextActiveTab is ready to be switched to

    // Make the next tab (new or existing) active
    this.activeTabId = nextActiveTab.id

    if (ActionStore.actions.length > 0) {
      LayoutStore.updateActions({ visible: true })
    } else {
      LayoutStore.updateActions({ visible: false })
    }
  }

  /**
   * Close a tab by ID
   */
  @action
  remove = (tabId: string): Tab => {
    const tab = this.get(tabId)

    if (!tab) {
      return this.get(MODEL_LIST_TAB_ID)!
    }

    const activeTabIndex = this.openTabs.findIndex(
      t => t.id === this.activeTabId
    )
    const closedTabIndex = this.openTabs.findIndex(t => t.id === tabId)

    // Remove the tab
    const removedTab = super.remove(tabId)

    // Remove any actions attached to this tab's session as well
    Object.values(ActionStore.values)
      .filter(a => a.sessionId === removedTab.sessionId)
      .forEach(a => ActionStore.remove(a.id))

    // We're not removing any scripts and sessions that were open in this tab because the next time the user opens Studio, BootstrapStore will do it for us

    // Unless the active tab was closed, there's nothing else to do.

    if (activeTabIndex === closedTabIndex) {
      // If the active tab was closed, we need to find another suitable tab to make active
      // First try to switch to the previous tab
      let nextActiveTabIndex = closedTabIndex - 1
      if (nextActiveTabIndex === -1) {
        // If the next tab would be the model list tab, try to switch to the next tab
        nextActiveTabIndex = closedTabIndex // Not +1, because there is one less tab now
      }

      this.switch({
        id: this.openTabs[nextActiveTabIndex]?.id ?? MODEL_LIST_TAB_ID,
      }) // Fall back to the model list tab if no other tabs exist
    }

    return removedTab
  }
}

export default new TabStore()
